import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyTutorStudents, getStudentWeeklyPracticePlan } from '../api/me';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { postCreateSession } from '../api/tutor';
import { resendLinkedStudentInvites } from '../api/admin';
import ActiveSessionConflictPrompt from '../components/ActiveSessionConflictPrompt';
import useActiveSessionConflict, {
  createSessionRequestError,
} from '../components/useActiveSessionConflict';
import SessionLengthSelector from '../components/SessionLengthSelector';
import { GUARDIAN_UNLOCK_REQUIREMENT, STAFF_GUARDIAN_NEXT_STEP } from '../copy/onboardingStatus';
import WeeklyPracticePlan from '../components/WeeklyPracticePlan';
import { buildSessionLengthPayload, DEFAULT_SESSION_LENGTH } from '../utils/sessionLength';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function getWeeklyPlanRecommendedSkillCode(plan) {
  const recommendedItem = (Array.isArray(plan?.items) ? plan.items : []).find(
    (item) => String(item?.recommended_skill_code || '').trim()
  );
  return String(recommendedItem?.recommended_skill_code || '').trim();
}

function TutorStudentsPage() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const role = user?.role || null;
  const userId = user?.id;
  const [students, setStudents] = useState([]);
  const [weeklyPlansByStudentId, setWeeklyPlansByStudentId] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [startingStudentId, setStartingStudentId] = useState(null);
  const [resendingStudentId, setResendingStudentId] = useState(null);
  const [inviteMessageByStudentId, setInviteMessageByStudentId] = useState({});
  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      setErrorMessage('');
      try {
        const response = await getMyTutorStudents();
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(extractErrorMessage(data, 'Failed to load students.'));
        }
        if (isMounted) {
          setStudents(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'Failed to load students.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadStudents();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!students.length) {
      setWeeklyPlansByStudentId({});
      return () => {
        isMounted = false;
      };
    }

    const loadWeeklyPlans = async () => {
      const entries = await Promise.all(
        students.map(async (student) => {
          try {
            const response = await getStudentWeeklyPracticePlan(student.id);
            const data = await parseJsonSafe(response);
            if (!response.ok) {
              throw new Error(extractErrorMessage(data, 'Failed to load weekly practice plan.'));
            }
            return [student.id, { plan: data, error: '' }];
          } catch (error) {
            return [student.id, { plan: null, error: error.message || 'Failed to load weekly practice plan.' }];
          }
        })
      );
      if (isMounted) {
        setWeeklyPlansByStudentId(Object.fromEntries(entries));
      }
    };

    loadWeeklyPlans();
    return () => {
      isMounted = false;
    };
  }, [students]);

  // Never silently reuse and never silently end an existing session: the same
  // two choices practice and the dashboard offer, from the same implementation.
  const activeSessionConflict = useActiveSessionConflict({
    navigate,
    onEndError: (error) => setErrorMessage(error?.message || 'Failed to end the existing session.'),
    onRetryError: (error) => setErrorMessage(error?.message || 'Failed to create tutor session.'),
  });

  const handleStartTutorSession = async (student) => {
    if (!userId) {
      setErrorMessage('Could not start session for this account.');
      return;
    }
    setErrorMessage('');
    const recommendedSkillCode = getWeeklyPlanRecommendedSkillCode(
      weeklyPlansByStudentId[student.id]?.plan
    );
    if (!recommendedSkillCode) {
      setErrorMessage(
        'No beta-ready topic is available for this student yet. A tutor session can start once their weekly practice plan recommends one.'
      );
      return;
    }

    const payload = {
      student_id: student.id,
      tutor_user_id: userId,
      exam_mode: false,
      track: 'DOUBLE_AWARD_2025',
      tier: 'FOUNDATION',
      selection_mode: 'single_topic',
      skill_code: recommendedSkillCode,
      ...buildSessionLengthPayload(sessionLength),
    };

    // Closed over the exact payload, so "End it and start a new session" retries
    // the original request rather than a re-derived approximation of it.
    const createSession = async () => {
      setStartingStudentId(student.id);
      try {
        const response = await postCreateSession(payload);
        const data = await parseJsonSafe(response);
        if (!response.ok) {
          if (
            response.status === 403
            && data?.detail === 'guardian_verification_required'
          ) {
            throw new Error(
              `${GUARDIAN_UNLOCK_REQUIREMENT} ${STAFF_GUARDIAN_NEXT_STEP}`,
            );
          }
          throw createSessionRequestError(response, data, 'Failed to create tutor session.');
        }
        const createdSessionId = data?.session_id ?? data?.id;
        if (!createdSessionId) {
          throw new Error('Session created but id was missing from response.');
        }
        navigate(`/sessions/${createdSessionId}/tutor`);
      } finally {
        setStartingStudentId(null);
      }
    };

    try {
      await activeSessionConflict.guardCreate(createSession);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create tutor session.');
    }
  };

  const handleResendInvite = async (student) => {
    setResendingStudentId(student.id);
    setInviteMessageByStudentId((messages) => ({ ...messages, [student.id]: '' }));
    try {
      const response = await resendLinkedStudentInvites(student.id);
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to resend invite.'));
      }
      setInviteMessageByStudentId((messages) => ({
        ...messages,
        [student.id]: 'Invite email resent to pending student or parent accounts.',
      }));
    } catch (error) {
      setInviteMessageByStudentId((messages) => ({
        ...messages,
        [student.id]: error.message || 'Failed to resend invite.',
      }));
    } finally {
      setResendingStudentId(null);
    }
  };

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Tutor workspace</p>
          <h1>My students</h1>
          <p>Linked students, quick tutor-session launch, and weekly mastery plan context for supported tutor workflows.</p>
        </div>
        <div className="page-header-actions">
          <Link to="/tutor-links" className="btn btn-secondary">Link an existing student</Link>
          <Link to="/admin/users/create" className="btn btn-primary">Open user creation wizard</Link>
        </div>
      </section>
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Teaching</p>
            <h2>Linked students</h2>
          </div>
        </div>
        <SessionLengthSelector
          value={sessionLength}
          onChange={setSessionLength}
          name="tutor-students-session-length"
          className="session-length-selector-compact"
          description="Choose the default session length for quick tutor launches."
        />
        {isLoading ? <p>Loading students...</p> : null}
        {!isLoading ? (
          <ul className="data-list">
            {students.map((student) => {
              const displayName = student.display_name || student.student_display_name || `Student ${student.id}`;
              const weeklyPlanState = weeklyPlansByStudentId[student.id] || null;
              return (
                <li key={student.id} className="data-list-item">
                  <div className="list-item-header">
                    <div>
                      <strong>{displayName}</strong>
                      <p className="muted-text">Student id: {student.id}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!weeklyPlanState || startingStudentId === student.id}
                    onClick={() => handleStartTutorSession(student)}
                  >
                    {!weeklyPlanState
                      ? 'Loading practice plan...'
                      : startingStudentId === student.id
                        ? 'Starting...'
                        : 'Start tutor session'}
                  </button>
                  {student.invite_pending || ['guardian_pending', 'tutor_pending'].includes(student.account_state) ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={resendingStudentId === student.id}
                      onClick={() => handleResendInvite(student)}
                    >
                      {resendingStudentId === student.id ? 'Resending...' : 'Resend invite'}
                    </button>
                  ) : null}
                  {inviteMessageByStudentId[student.id] ? (
                    <p role="status" className="notice notice-info">
                      {inviteMessageByStudentId[student.id]}
                    </p>
                  ) : null}
                  {weeklyPlanState?.plan ? (
                    <WeeklyPracticePlan
                      plan={weeklyPlanState.plan}
                      compact
                      title="Weekly mastery practice plan"
                    />
                  ) : null}
                  {weeklyPlanState?.error ? (
                    <p role="alert" className="notice notice-error">{weeklyPlanState.error}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {!isLoading && !errorMessage && students.length === 0 ? (
          <div className="notice notice-info">
            <p>No linked students yet.</p>
            <p>Ask an admin to link a student to your tutor account.</p>
            {role === 'ADMIN' ? (
              <p>
                <Link to="/admin/users">Open Admin: Users</Link>
              </p>
            ) : null}
          </div>
        ) : null}
        {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
        <ActiveSessionConflictPrompt
          conflict={activeSessionConflict.conflict}
          isResolving={activeSessionConflict.isResolving}
          onResume={activeSessionConflict.resume}
          onEndAndStartNew={activeSessionConflict.endAndStartNew}
          titleId="tutor-students-active-session-conflict-title"
        />
      </section>
    </div>
  );
}

export default TutorStudentsPage;
